/**
 * Country Context — Global country configuration layer
 *
 * Loads country config from backend, provides:
 * - Country config (currency, cities, property types, etc.)
 * - Dynamic formatting utilities
 * - Feature flag checks
 * - UAE fallback safety: if any config load fails, UAE defaults apply
 *
 * Does NOT modify any existing Dubai logic/UI.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CountryContext = createContext();

// UAE fallback — hardcoded safety net so the app never breaks
const UAE_FALLBACK = {
  code: 'uae',
  name: 'United Arab Emirates',
  flag: 'ae',
  active: true,
  default: true,
  currency: {
    code: 'AED',
    symbol: 'AED',
    name: 'UAE Dirham',
    number_system: 'international',
    locale: 'en-AE',
  },
  budgets: {
    property: { min: 1000000, max: 50000000 },
    car: { min: 500000, max: 10000000 },
    lifestyle: { min: 100000, max: 5000000 },
  },
  cities: {
    premium: [
      { value: 'dubai', label: 'Dubai' },
      { value: 'abu_dhabi', label: 'Abu Dhabi' },
      { value: 'sharjah', label: 'Sharjah' },
    ],
  },
  communities: [
    'Palm Jumeirah', 'Downtown Dubai', 'Dubai Hills Estate',
    'Emirates Hills', 'Dubai Marina', 'Arabian Ranches',
  ],
  property_types: ['Apartment', 'Villa', 'Penthouse', 'Mansion', 'Townhouse'],
  view_types: ['Sea View', 'Skyline View', 'Golf Course View', 'Desert View', 'Marina View', 'Park View'],
  feature_flags: {
    subscriptions: true,
    ai_bundles: true,
    marketplace: true,
    concierge: true,
    partner_portal: true,
    virtual_tours: true,
    investor_mode: true,
    portfolio_insights: true,
    lead_generation: false,
  },
  scoring_weights: {},
  roi_assumptions: {},
  monetization: { model: 'subscription' },
  bundle_names: {},
  bundle_taglines: {},
  developers: [],
};

export const CountryProvider = ({ children }) => {
  // Initialize from localStorage if available, otherwise default to 'uae'
  const [countryCode, setCountryCodeState] = useState(() => {
    const saved = localStorage.getItem('dreamlife_country');
    return saved && ['uae', 'india'].includes(saved) ? saved : 'uae';
  });
  const [config, setConfig] = useState(UAE_FALLBACK);
  const [availableCountries, setAvailableCountries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Load available countries on mount
  useEffect(() => {
    const init = async () => {
      try {
        const res = await axios.get(`${API}/countries`).catch(() => null);
        if (res?.data?.countries) {
          setAvailableCountries(res.data.countries);
        }
      } catch (e) { /* fallback to empty */ }

      // Load config for the current country (from localStorage or default)
      const currentCode = localStorage.getItem('dreamlife_country') || 'uae';
      if (currentCode !== 'uae') {
        try {
          const cfgRes = await axios.get(`${API}/countries/${currentCode}/config`);
          if (cfgRes?.data?.config) {
            const merged = deepMerge(UAE_FALLBACK, cfgRes.data.config);
            setConfig(merged);
            setCountryCodeState(currentCode);
          }
        } catch (e) { /* fallback to UAE */ }
      }

      setLoaded(true);
    };
    init();
  }, []);

  const mapCountryNameToCode = (name) => {
    const map = {
      'United Arab Emirates': 'uae',
      'India': 'india',
      'Saudi Arabia': 'saudi',
      'United Kingdom': 'uk',
    };
    return map[name] || 'uae';
  };

  const loadConfig = useCallback(async (code) => {
    try {
      const res = await axios.get(`${API}/countries/${code}/config`);
      if (res?.data?.config) {
        const merged = deepMerge(UAE_FALLBACK, res.data.config);
        setConfig(merged);
        setCountryCodeState(code);
        localStorage.setItem('dreamlife_country', code);
        return merged;
      }
    } catch (e) {
      setConfig(UAE_FALLBACK);
      setCountryCodeState('uae');
      localStorage.setItem('dreamlife_country', 'uae');
    }
    return UAE_FALLBACK;
  }, []);

  const setCountry = useCallback(async (code) => {
    localStorage.setItem('dreamlife_country', code);
    await loadConfig(code);
    // Track analytics
    try {
      
      if (token) {
        await axios.post(`${API}/countries/${code}/analytics/track`,
          { event: 'country_switch', metadata: { from: countryCode, to: code } },
          { withCredentials: true }
        ).catch(() => null);
      }
    } catch (e) { /* silent */ }
  }, [countryCode, loadConfig]);

  // ── Feature flag check ──
  const isFeatureEnabled = useCallback((feature) => {
    return config.feature_flags?.[feature] ?? UAE_FALLBACK.feature_flags[feature] ?? false;
  }, [config]);

  // ── Dynamic number formatting ──
  const formatNumber = useCallback((amount) => {
    if (!amount && amount !== 0) return '0';
    const abs = Math.abs(amount);
    const isIndian = config.currency?.number_system === 'indian';
    const sym = config.currency?.symbol || 'AED';

    if (isIndian) {
      if (abs >= 10_000_000) return `${sym}${(amount / 10_000_000).toFixed(abs >= 100_000_000 ? 0 : 1)}Cr`;
      if (abs >= 100_000) return `${sym}${(amount / 100_000).toFixed(abs >= 1_000_000 ? 0 : 1)}L`;
      if (abs >= 1_000) return `${sym}${Math.round(amount).toLocaleString('en-IN')}`;
      return `${sym}${amount.toFixed(2)}`;
    }

    if (abs >= 1_000_000_000) return `${sym}${(amount / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `${sym}${(amount / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sym}${Math.round(amount).toLocaleString()}`;
    return `${sym}${amount.toFixed(2)}`;
  }, [config]);

  const value = {
    countryCode,
    config,
    setCountry,
    availableCountries,
    isFeatureEnabled,
    formatNumber,
    loaded,
    isDefault: countryCode === 'uae',
  };

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
};

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error('useCountry must be used within CountryProvider');
  return ctx;
};

// Deep merge utility
function deepMerge(base, overlay) {
  const result = { ...base };
  for (const key of Object.keys(overlay)) {
    if (
      overlay[key] !== null &&
      overlay[key] !== undefined &&
      typeof overlay[key] === 'object' &&
      !Array.isArray(overlay[key]) &&
      typeof result[key] === 'object' &&
      !Array.isArray(result[key])
    ) {
      result[key] = deepMerge(result[key], overlay[key]);
    } else if (overlay[key] !== null && overlay[key] !== undefined) {
      result[key] = overlay[key];
    }
  }
  return result;
}
